import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import isInCCLiframe from '../../utils/isInCCLiframe';
import { setSelectedOrganisation } from '../../utils/getOrgMembership';
import userHasV3Permission from '../../utils/userHasV3Permission';
import { GET_USER_BY_ID_V3 } from '../../apollo/queries/v3/getUserByIdV3.queries';
import {
  GET_PERMISSIONS_BY_USER_ID,
  UserPermissionResponse,
} from '../../apollo/queries/v3/RolesNPermissions/getPermissionsByUserId.queries';
import appLocalStorage from '../../utils/AppLocalStorage';

type Props = {
  isFromMembersPage?: boolean;
};

const useIframeAuth = ({ isFromMembersPage = false }: Props = {}) => {
  const params = useParams<{ code: string }>();
  const [userDataAvailable, setUserDataAvailable] = useState(false);
  const [permissionsAvailable, setPermissionsAvailable] = useState(false);

  const getFromSearchParam = (name: string) =>
    `${new URLSearchParams((window as any).location.search).get(name)}`;

  const cclUser = appLocalStorage.getAlefCclUser();
  const cclUserId = cclUser && cclUser.user && cclUser.user.id;
  const paramUserId = getFromSearchParam('userId');
  const userId = paramUserId !== 'null' ? paramUserId : cclUserId;

  const [getUser] = useLazyQuery(GET_USER_BY_ID_V3, {
    onCompleted: (data) => {
      if (data.userByIdV3) {
        appLocalStorage.setAlefCclUser(data.userByIdV3);
      }
      if (!userDataAvailable) setUserDataAvailable(true);
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const [getUserPermissionsById] = useLazyQuery<UserPermissionResponse>(
    GET_PERMISSIONS_BY_USER_ID,
    {
      onCompleted: (data) => {
        const { getPermissionsByUserId } = data;

        if (getPermissionsByUserId && getPermissionsByUserId.permissions) {
          appLocalStorage.setUserPermissions(
            getPermissionsByUserId.permissions,
          );
          if (!permissionsAvailable) setPermissionsAvailable(true);
        }
      },
      onError: (e) => {
        console.log(e);
      },
    },
  );

  const getUserAPI = () => {
    // This is to fetch old roles. Should be cleaned up once all users has V3 permissions.
    const options = { variables: { id: userId } };
    getUser(options);
  };

  const getPermissionsAPI = () => {
    const options = { variables: { id: userId } };
    getUserPermissionsById(options);
  };

  useEffect(() => {
    if (isInCCLiframe) {
      if (isFromMembersPage) {
        setSelectedOrganisation(params.code);
        getUserAPI();
        getPermissionsAPI();
      } else {
        const userExist = cclUser && Object.keys(cclUser).length > 0;

        if (userExist) {
          setUserDataAvailable(true);
        }

        const permissionsExist = appLocalStorage.getPermissions();

        if (permissionsExist) {
          setPermissionsAvailable(true);
        }
      }
    }
  }, []);

  const isAuthorized = (permission: string) => {
    if (!isInCCLiframe) return true;
    else {
      return userDataAvailable && permissionsAvailable
        ? userHasV3Permission(permission)
        : false;
    }
  };

  return {
    isAuthorized,
    userDataAvailable,
    permissionsAvailable,
  };
};

export default useIframeAuth;
